import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

/**
 * Theme has three states, not two:
 *   "light" / "dark" — an explicit user choice, written to <html data-theme>
 *   "system"         — no attribute at all, so tokens.css falls through to
 *                      @media (prefers-color-scheme: dark)
 *
 * Keeping "system" as a real state (rather than resolving it once at boot) is
 * what lets the site follow a phone that switches to dark at sunset.
 */
const STORAGE_KEY = "wh-theme";
const ThemeContext = createContext(null);

const prefersDark = () =>
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(prefers-color-scheme: dark)").matches;

function readStored() {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === "light" || v === "dark" ? v : "system";
  } catch (_) {
    return "system"; // private mode / storage disabled
  }
}

function applyTheme(theme) {
  const root = document.documentElement;
  if (theme === "system") root.removeAttribute("data-theme");
  else root.setAttribute("data-theme", theme);
}

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(readStored);
  const [systemDark, setSystemDark] = useState(prefersDark);

  // Track the OS setting so `resolved` stays correct while in "system" mode.
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = (e) => setSystemDark(e.matches);
    mq.addEventListener ? mq.addEventListener("change", onChange) : mq.addListener(onChange);
    return () => {
      mq.removeEventListener
        ? mq.removeEventListener("change", onChange)
        : mq.removeListener(onChange);
    };
  }, []);

  useEffect(() => {
    applyTheme(theme);
    try {
      if (theme === "system") localStorage.removeItem(STORAGE_KEY);
      else localStorage.setItem(STORAGE_KEY, theme);
    } catch (_) {
      /* storage unavailable — the attribute is still applied for this session */
    }
  }, [theme]);

  const resolved = theme === "system" ? (systemDark ? "dark" : "light") : theme;

  const setTheme = useCallback((next) => {
    setThemeState(next === "light" || next === "dark" ? next : "system");
  }, []);

  const toggle = useCallback(() => {
    setThemeState(resolved === "dark" ? "light" : "dark");
  }, [resolved]);

  const value = useMemo(
    () => ({ theme, resolved, setTheme, toggle }),
    [theme, resolved, setTheme, toggle]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used inside <ThemeProvider>");
  return ctx;
}
